import type { ReactNode } from 'react';
import { I18nConsumer, I18nLanguage, I18nProvider } from '@wegic/i18n-core';
import { useCallback, useEffect } from 'react';
import { useI18nStore } from '@/stores/i18n';
import { useUserStore } from '@/stores/user';
import { BUILDER_I18N_NAMESPACE, builderI18nLoaders, defaultLanguage } from './const';
import { fetchAuthedLanguage, getLanguageSetting, setLanguageSetting } from './helper';

interface I18nProviderProps {
  children: ReactNode;
}

function BuilderI18nProvider({ children }: I18nProviderProps) {
  const { changeLanguage } = useI18nStore.getState();
  const currentLanguage = useI18nStore(state => state.currentLanguage);

  /**
   * 切换语言
   */
  const handleLocaleChange = useCallback((language: string) => {
    setLanguageSetting(language);
    changeLanguage(language);
  }, []);

  /**
   * 初始化国际化
   * 优先级：云端设置 > Cookie > IP语言 > 浏览器语言 > 默认英语
   * 匿名访问：直接走本地缓存
   */
  useEffect(() => {
    async function initializeIntl() {
      const local = getLanguageSetting() || defaultLanguage;
      const authed = useUserStore.getState().isLogin
        ? await fetchAuthedLanguage()
        : null;
      const finalLanguage = authed ?? local;
      setLanguageSetting(finalLanguage);
      changeLanguage(finalLanguage);
    }

    initializeIntl();
  }, []);

  // 语言未确定前不渲染
  if (!currentLanguage) {
    return null;
  }

  return (
    <I18nProvider locale={currentLanguage} onLocaleChange={handleLocaleChange} fallbackLocale={I18nLanguage.en}>
      <I18nConsumer loaders={builderI18nLoaders} namespace={BUILDER_I18N_NAMESPACE}>
        {children}
      </I18nConsumer>
    </I18nProvider>
  );
}

export default BuilderI18nProvider;
